import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './marketing.css';

const TOC = [
  'Who We Are',
  'What Data We Collect',
  'Legal Basis for Processing (GDPR)',
  'How We Use Your Data',
  'AI & Machine Learning — Your Data',
  'Data Sharing & Subprocessors',
  'International Data Transfers',
  'Data Security',
  'Data Retention Schedule',
  'Cookies & Analytics',
  'Marketing Communications',
  'Your Rights',
  'California Residents (CCPA)',
  'Data Breach Notification',
  'Children\'s Privacy',
  'Changes to This Policy',
  'Contact Us',
];

export default function Privacy() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);

  const toggle = (i) => setOpen(open === i ? null : i);

  const sections = [
    {
      title: '1. Who We Are',
      body: (
        <>
          <p>Gantima Software ("we", "us", "our") is an AI automation agency that builds business process automation, AI chatbots, custom SaaS products, and trading strategy automation systems for businesses worldwide.</p>
          <p style={{marginTop:'12px'}}>Our website is <strong>www.gantima.com</strong>. For privacy-related enquiries, contact us at <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>.</p>
          <p style={{marginTop:'12px'}}>This policy applies to all visitors and users of www.gantima.com and any services provided by Gantima Software.</p>
        </>
      ),
    },
    {
      title: '2. What Data We Collect',
      body: (
        <>
          <p style={{marginBottom:'14px'}}>We collect data in two ways:</p>
          <p style={{fontWeight:700,color:'#e2e8f0',marginBottom:'8px'}}>A. Data you give us directly</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginBottom:'20px'}}>
            <li>Name, company name, email address, phone number</li>
            <li>Information about your business, workflows, and automation goals</li>
            <li>Job application details: LinkedIn, portfolio URL, cover letter, resume link</li>
            <li>Messages sent through our contact or booking forms</li>
          </ul>
          <p style={{fontWeight:700,color:'#e2e8f0',marginBottom:'8px'}}>B. Data collected automatically</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px'}}>
            <li>Page views, session duration, traffic source (via Google Analytics — anonymised)</li>
            <li>Device type, browser, operating system, screen resolution</li>
            <li>Country and approximate city (IP-based, not stored)</li>
            <li>Referring URL</li>
          </ul>
          <p style={{marginTop:'14px'}}>We do <strong style={{color:'#e2e8f0'}}>not</strong> collect payment card details, government IDs, or sensitive personal data.</p>
        </>
      ),
    },
    {
      title: '3. Legal Basis for Processing (GDPR)',
      body: (
        <>
          <p style={{marginBottom:'16px'}}>Under GDPR Article 6, we rely on the following legal bases for processing personal data:</p>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.85rem'}}>
              <thead>
                <tr style={{background:'#0f2040',color:'#e2e8f0'}}>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Processing Activity</th>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Responding to consultation / enquiry forms','Legitimate interest (Art. 6(1)(f)) / Pre-contractual steps (Art. 6(1)(b))'],
                  ['Processing job applications','Legitimate interest (Art. 6(1)(f))'],
                  ['Sending marketing emails (with opt-in)','Consent (Art. 6(1)(a))'],
                  ['Website analytics via Google Analytics','Legitimate interest (Art. 6(1)(f)) — anonymised data'],
                  ['Complying with legal obligations','Legal obligation (Art. 6(1)(c))'],
                ].map(([act, basis], i) => (
                  <tr key={i} style={{background: i % 2 === 0 ? '#0a1628' : '#0d1b30'}}>
                    <td style={{padding:'10px 14px',color:'#94a3b8',border:'1px solid rgba(79,142,247,0.1)'}}>{act}</td>
                    <td style={{padding:'10px 14px',color:'#7dd3fc',border:'1px solid rgba(79,142,247,0.1)'}}>{basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{marginTop:'14px'}}>Where we rely on consent, you may withdraw it at any time by emailing <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>.</p>
        </>
      ),
    },
    {
      title: '4. How We Use Your Data',
      body: (
        <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'10px'}}>
          <li>Respond to your consultation request, enquiry, or project brief</li>
          <li>Review and process job applications</li>
          <li>Send service updates or relevant information (only with your consent)</li>
          <li>Improve our website content, performance, and user experience</li>
          <li>Comply with applicable legal and regulatory obligations</li>
          <li>Detect and prevent fraud or abuse</li>
        </ul>
      ),
    },
    {
      title: '5. AI & Machine Learning — Your Data',
      body: (
        <>
          <div style={{background:'rgba(52,211,153,0.06)',border:'1px solid rgba(52,211,153,0.2)',borderRadius:'12px',padding:'18px 20px',marginBottom:'16px'}}>
            <p style={{color:'#34d399',fontWeight:700,marginBottom:'6px'}}>Our commitment:</p>
            <p style={{color:'#94a3b8'}}>We do <strong style={{color:'#e2e8f0'}}>not</strong> use any data you submit through our website (form submissions, enquiries, job applications) to train, fine-tune, or improve any AI or machine learning model — ours or anyone else's.</p>
          </div>
          <p>When we build AI automation systems for clients, those engagements are governed by a separate client agreement and data processing addendum (DPA). Client data used in AI systems is processed strictly according to the client's instructions and is never shared, sold, or used for any purpose beyond the agreed scope of work.</p>
          <p style={{marginTop:'12px'}}>The AI tools we use internally (e.g. GPT-4, Claude) are accessed via their APIs. We do not share identifiable visitor or enquirer data with these tools.</p>
        </>
      ),
    },
    {
      title: '6. Data Sharing & Subprocessors',
      body: (
        <>
          <p style={{marginBottom:'16px'}}>We do not sell or rent your personal data. We share data only with the following trusted subprocessors who help us operate our website and business:</p>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.85rem'}}>
              <thead>
                <tr style={{background:'#0f2040',color:'#e2e8f0'}}>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Subprocessor</th>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Purpose</th>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Location</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Formspree','Form submission processing','USA (GDPR SCCs apply)'],
                  ['Amazon Web Services (AWS)','Website hosting via Amplify','USA / Global (GDPR SCCs apply)'],
                  ['Google Analytics 4','Anonymised website analytics','USA (GDPR SCCs apply)'],
                  ['Google Fonts','Typography / font delivery','USA (no personal data)'],
                  ['Google Workspace','Internal email & documents','USA (GDPR SCCs apply)'],
                ].map(([name, purpose, loc], i) => (
                  <tr key={i} style={{background: i % 2 === 0 ? '#0a1628' : '#0d1b30'}}>
                    <td style={{padding:'10px 14px',color:'#e2e8f0',fontWeight:600,border:'1px solid rgba(79,142,247,0.1)'}}>{name}</td>
                    <td style={{padding:'10px 14px',color:'#94a3b8',border:'1px solid rgba(79,142,247,0.1)'}}>{purpose}</td>
                    <td style={{padding:'10px 14px',color:'#7dd3fc',border:'1px solid rgba(79,142,247,0.1)'}}>{loc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{marginTop:'14px'}}>We may also disclose data to law enforcement or regulators if legally required to do so.</p>
        </>
      ),
    },
    {
      title: '7. International Data Transfers',
      body: (
        <>
          <p>Gantima Software operates globally. Some of our subprocessors are based in the United States. When we transfer personal data from the EEA or UK to the USA, we ensure appropriate safeguards are in place, including:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',margin:'12px 0'}}>
            <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
            <li>UK International Data Transfer Agreements (IDTAs) where applicable</li>
            <li>Adequacy decisions where they exist</li>
          </ul>
          <p>You may request a copy of the applicable transfer safeguards by emailing <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>.</p>
        </>
      ),
    },
    {
      title: '8. Data Security',
      body: (
        <>
          <p style={{marginBottom:'14px'}}>We take the security of your data seriously. Our measures include:</p>
          <ul style={{listStyle:'none',display:'flex',flexDirection:'column',gap:'10px'}}>
            {[
              ['🔒','Encryption in transit','All data transmitted to/from our website is encrypted using TLS 1.2 or higher (HTTPS).'],
              ['🔐','Encryption at rest','Data stored in our systems is encrypted at rest using AES-256.'],
              ['🛡️','Access controls','Personal data is accessible only to authorised Gantima team members on a need-to-know basis.'],
              ['☁️','Secure hosting','Our website is hosted on AWS Amplify, which is ISO 27001 and SOC 2 certified.'],
              ['📝','Form security','Form submissions are processed via Formspree over encrypted connections and delivered directly to our secure inbox.'],
            ].map(([icon, title, desc], i) => (
              <li key={i} style={{display:'flex',gap:'14px',background:'#0f2040',borderRadius:'10px',padding:'14px 16px'}}>
                <span style={{fontSize:'1.3rem',flexShrink:0}}>{icon}</span>
                <div><strong style={{color:'#e2e8f0',display:'block',marginBottom:'4px'}}>{title}</strong><span style={{color:'#94a3b8',fontSize:'.85rem'}}>{desc}</span></div>
              </li>
            ))}
          </ul>
          <p style={{marginTop:'14px'}}>No method of transmission or storage is 100% secure. If you have security concerns, contact us at <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>.</p>
        </>
      ),
    },
    {
      title: '9. Data Retention Schedule',
      body: (
        <>
          <p style={{marginBottom:'16px'}}>We retain different types of data for different periods based on business need and legal requirements:</p>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'.85rem'}}>
              <thead>
                <tr style={{background:'#0f2040',color:'#e2e8f0'}}>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Data Type</th>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Retention Period</th>
                  <th style={{padding:'10px 14px',textAlign:'left',border:'1px solid rgba(79,142,247,0.15)'}}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Consultation / enquiry form data','12 months','Follow-up and business records'],
                  ['Job application data','6 months after decision','Legal and HR compliance'],
                  ['Google Analytics data','14 months','Standard GA4 retention limit'],
                  ['Email correspondence','24 months','Business records'],
                  ['Legal / compliance records','7 years','Statutory requirement'],
                ].map(([type, period, reason], i) => (
                  <tr key={i} style={{background: i % 2 === 0 ? '#0a1628' : '#0d1b30'}}>
                    <td style={{padding:'10px 14px',color:'#e2e8f0',border:'1px solid rgba(79,142,247,0.1)'}}>{type}</td>
                    <td style={{padding:'10px 14px',color:'#f59e0b',fontWeight:600,border:'1px solid rgba(79,142,247,0.1)'}}>{period}</td>
                    <td style={{padding:'10px 14px',color:'#94a3b8',border:'1px solid rgba(79,142,247,0.1)'}}>{reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{marginTop:'14px'}}>After the retention period, data is securely deleted or anonymised. You may request earlier deletion at any time.</p>
        </>
      ),
    },
    {
      title: '10. Cookies & Analytics',
      body: (
        <>
          <p style={{marginBottom:'14px'}}>Our website uses the following types of cookies:</p>
          <ul style={{listStyle:'none',display:'flex',flexDirection:'column',gap:'10px',marginBottom:'16px'}}>
            {[
              ['Essential cookies','Required for the website to function. Cannot be disabled.','Always active'],
              ['Analytics cookies (GA4)','Help us understand how visitors use the site. Data is anonymised and does not personally identify you.','Optional — analytics.google.com/gaoptout'],
            ].map(([name, desc, control], i) => (
              <li key={i} style={{background:'#0f2040',borderRadius:'10px',padding:'14px 16px'}}>
                <strong style={{color:'#e2e8f0',display:'block',marginBottom:'4px'}}>{name}</strong>
                <span style={{color:'#94a3b8',fontSize:'.85rem',display:'block',marginBottom:'4px'}}>{desc}</span>
                <span style={{color:'#4f8ef7',fontSize:'.78rem'}}>Control: {control}</span>
              </li>
            ))}
          </ul>
          <p>We do <strong style={{color:'#e2e8f0'}}>not</strong> use advertising, tracking, retargeting, or social media cookies. You can control cookies through your browser settings.</p>
        </>
      ),
    },
    {
      title: '11. Marketing Communications',
      body: (
        <>
          <p>We will only send you marketing or promotional emails if you have explicitly opted in (e.g. by ticking a consent box on our form).</p>
          <p style={{marginTop:'12px'}}>Every marketing email includes an <strong style={{color:'#e2e8f0'}}>unsubscribe link</strong>. You may also opt out at any time by emailing <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a> with the subject line "Unsubscribe".</p>
          <p style={{marginTop:'12px'}}>Transactional emails (e.g. a reply to your consultation request) are not marketing and do not require opt-in.</p>
        </>
      ),
    },
    {
      title: '12. Your Rights',
      body: (
        <>
          <p style={{marginBottom:'14px'}}>Depending on your location, you have the following rights over your personal data:</p>
          <ul style={{listStyle:'none',display:'flex',flexDirection:'column',gap:'10px',marginBottom:'16px'}}>
            {[
              ['Access','Request a copy of all personal data we hold about you.'],
              ['Rectification','Ask us to correct any inaccurate or incomplete data.'],
              ['Erasure ("Right to be Forgotten")','Ask us to delete your personal data. We will comply unless we have a legal obligation to retain it.'],
              ['Restriction','Ask us to pause processing of your data while a dispute is resolved.'],
              ['Portability','Request your data in a structured, machine-readable format (e.g. CSV).'],
              ['Objection','Object to processing based on legitimate interest. We will stop unless we have compelling grounds.'],
              ['Withdraw Consent','Where processing is based on consent, withdraw it at any time.'],
            ].map(([right, desc], i) => (
              <li key={i} style={{display:'flex',gap:'12px',background:'#0f2040',borderRadius:'10px',padding:'14px 16px'}}>
                <span style={{color:'#4f8ef7',fontWeight:800,flexShrink:0,minWidth:'24px'}}>→</span>
                <div><strong style={{color:'#e2e8f0',display:'block',marginBottom:'4px'}}>{right}</strong><span style={{color:'#94a3b8',fontSize:'.85rem'}}>{desc}</span></div>
              </li>
            ))}
          </ul>
          <p>To exercise any right, email <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>. We will respond within <strong style={{color:'#e2e8f0'}}>30 days</strong>. We may ask you to verify your identity before fulfilling the request.</p>
          <p style={{marginTop:'12px'}}>You also have the right to lodge a complaint with your national data protection authority (e.g. the ICO in the UK, or your EU member state's DPA).</p>
        </>
      ),
    },
    {
      title: '13. California Residents (CCPA / CPRA)',
      body: (
        <>
          <p style={{marginBottom:'14px'}}>If you are a California resident, you have the following rights under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA):</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',marginBottom:'16px'}}>
            <li><strong style={{color:'#e2e8f0'}}>Right to Know</strong> — what personal information we collect, use, share, or sell</li>
            <li><strong style={{color:'#e2e8f0'}}>Right to Delete</strong> — request deletion of your personal information</li>
            <li><strong style={{color:'#e2e8f0'}}>Right to Correct</strong> — request correction of inaccurate personal information</li>
            <li><strong style={{color:'#e2e8f0'}}>Right to Opt Out</strong> — opt out of the sale or sharing of personal information</li>
            <li><strong style={{color:'#e2e8f0'}}>Right to Non-Discrimination</strong> — we will not discriminate against you for exercising your rights</li>
          </ul>
          <div style={{background:'rgba(79,142,247,0.06)',border:'1px solid rgba(79,142,247,0.2)',borderRadius:'12px',padding:'16px 20px'}}>
            <p style={{color:'#4f8ef7',fontWeight:700,marginBottom:'6px'}}>Do Not Sell or Share My Personal Information</p>
            <p style={{color:'#94a3b8',fontSize:'.875rem'}}>We do <strong style={{color:'#e2e8f0'}}>not</strong> sell or share your personal information with third parties for cross-context behavioural advertising. To exercise any CCPA right, email <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>.</p>
          </div>
        </>
      ),
    },
    {
      title: '14. Data Breach Notification',
      body: (
        <>
          <p>In the event of a personal data breach that is likely to result in a risk to your rights and freedoms, we will:</p>
          <ul style={{listStyle:'disc',paddingLeft:'20px',display:'flex',flexDirection:'column',gap:'8px',margin:'12px 0'}}>
            <li>Notify the relevant supervisory authority (e.g. ICO) <strong style={{color:'#e2e8f0'}}>within 72 hours</strong> of becoming aware of the breach, as required by GDPR Article 33</li>
            <li>Notify affected individuals <strong style={{color:'#e2e8f0'}}>without undue delay</strong> where the breach is likely to result in high risk to their rights and freedoms, in accordance with GDPR Article 34</li>
            <li>Provide details of the nature of the breach, data categories affected, likely consequences, and measures taken or proposed</li>
          </ul>
          <p>If you believe your data has been compromised, contact us immediately at <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a>.</p>
        </>
      ),
    },
    {
      title: "15. Children's Privacy",
      body: (
        <p>Our services are intended for businesses and professionals aged 18 and over. We do not knowingly collect personal data from anyone under 16. If you believe a child has submitted data through our website, please contact us at <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a> and we will delete it promptly.</p>
      ),
    },
    {
      title: '16. Changes to This Policy',
      body: (
        <p>We review and update this policy periodically. When we make material changes, we will update the "Last updated" date at the top. We encourage you to review this page occasionally. Continued use of our website after changes have been posted constitutes your acceptance of the revised policy.</p>
      ),
    },
    {
      title: '17. Contact Us',
      body: (
        <>
          <p style={{marginBottom:'16px'}}>For all privacy-related questions, requests, or complaints:</p>
          <div style={{background:'#0a1628',border:'1px solid rgba(79,142,247,0.2)',borderRadius:'14px',padding:'24px'}}>
            <p style={{color:'#e2e8f0',fontWeight:800,fontSize:'1rem',marginBottom:'12px'}}>Gantima Software — Privacy Team</p>
            <p style={{marginBottom:'6px'}}>📧 <a href="mailto:support@gantima.com" style={{color:'#4f8ef7'}}>support@gantima.com</a></p>
            <p style={{marginBottom:'6px'}}>🌐 <a href="https://www.gantima.com" style={{color:'#4f8ef7'}}>www.gantima.com</a></p>
            <p style={{marginTop:'14px',fontSize:'.82rem',color:'#64748b'}}>We aim to respond to all privacy requests within 5 business days and will resolve them within 30 days.</p>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="mkt-root">
      {/* Nav */}
      <nav className="mkt-nav">
        <a href="/" className="mkt-nav-brand" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <img src="/GANTIMA-Logo.png" alt="Gantima" className="mkt-nav-logo-left-img" />
          <span className="mkt-nav-brand-tag">SOFTWARE AUTOMATION</span>
        </a>
        
        <div className="mkt-nav-brand" onClick={() => navigate('/')} style={{cursor:'pointer'}}>
          <img src="/gantima-icon.png" alt="Gantima Logo" className="mkt-nav-brand-icon" />
          <div className="mkt-nav-brand-text">
            <span className="mkt-nav-brand-name">GANTIMA</span>
            <span className="mkt-nav-brand-tag">AI AUTOMATION AGENCY</span>
          </div>
        </div>
        <ul className="mkt-nav-links">
          <li><a href="/" onClick={e=>{e.preventDefault();navigate('/');}}>Home</a></li>
          <li><a href="/careers" onClick={e=>{e.preventDefault();navigate('/careers');}}>Careers</a></li>
        </ul>
        <div className="mkt-nav-actions">
          <button className="mkt-btn mkt-btn-primary" onClick={() => navigate('/')}>Book Free Consultation</button>
        </div>
      </nav>

      <div style={{maxWidth:'860px',margin:'0 auto',padding:'140px 5% 80px'}}>
        {/* Header */}
        <div className="mkt-pill" style={{marginBottom:'20px'}}>Legal</div>
        <h1 style={{fontSize:'clamp(2rem,4vw,3rem)',fontWeight:900,marginBottom:'12px',color:'#e2e8f0'}}>Privacy Policy</h1>
        <p style={{color:'#64748b',fontSize:'.875rem',marginBottom:'8px'}}>Last updated: January 2025 &nbsp;·&nbsp; Version 2.0</p>
        <p style={{color:'#94a3b8',fontSize:'.9rem',marginBottom:'40px',maxWidth:'600px',lineHeight:1.7}}>
          We believe privacy should be transparent and understandable — not buried in legal jargon.
          This policy explains exactly what data we collect, why, and what rights you have over it.
        </p>

        {/* Table of Contents */}
        <div style={{background:'#0a1628',border:'1px solid rgba(79,142,247,0.15)',borderRadius:'16px',padding:'24px 28px',marginBottom:'40px'}}>
          <p style={{fontWeight:800,color:'#e2e8f0',marginBottom:'14px',fontSize:'.9rem',letterSpacing:'1px',textTransform:'uppercase'}}>Contents</p>
          <ol style={{listStyle:'none',display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:'8px'}}>
            {TOC.map((item, i) => (
              <li key={i}>
                <button
                  onClick={() => setOpen(i)}
                  style={{background:'none',border:'none',color:'#4f8ef7',fontSize:'.83rem',cursor:'pointer',textAlign:'left',padding:'2px 0',fontFamily:'inherit'}}
                >
                  {i+1}. {item}
                </button>
              </li>
            ))}
          </ol>
        </div>

        {/* Accordion Sections */}
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {sections.map((s, i) => (
            <div key={i} style={{background:'#0a1628',border:`1px solid ${open===i ? 'rgba(79,142,247,0.4)' : 'rgba(79,142,247,0.12)'}`,borderRadius:'14px',overflow:'hidden',transition:'border-color .2s'}}>
              <button
                onClick={() => toggle(i)}
                style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',background:'none',border:'none',cursor:'pointer',fontFamily:'inherit',textAlign:'left'}}
              >
                <span style={{fontWeight:800,fontSize:'1rem',color:'#e2e8f0'}}>{s.title}</span>
                <span style={{color:'#4f8ef7',fontSize:'1.2rem',transform: open===i ? 'rotate(180deg)' : 'rotate(0)',transition:'transform .2s',flexShrink:0,marginLeft:'16px'}}>▾</span>
              </button>
              {open === i && (
                <div style={{padding:'0 24px 24px',color:'#94a3b8',lineHeight:1.8,fontSize:'.9rem'}}>
                  {s.body}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div style={{marginTop:'56px',padding:'32px',background:'#0a1628',border:'1px solid rgba(79,142,247,0.15)',borderRadius:'16px',textAlign:'center'}}>
          <p style={{color:'#94a3b8',marginBottom:'20px'}}>Have a question about how we handle your data? We're happy to help.</p>
          <div style={{display:'flex',gap:'12px',justifyContent:'center',flexWrap:'wrap'}}>
            <a href="mailto:support@gantima.com" className="mkt-btn mkt-btn-outline">📧 Email Privacy Team</a>
            <button className="mkt-btn mkt-btn-primary" onClick={() => navigate('/')}>← Back to Home</button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mkt-footer">
        <div className="mkt-container">
          <div className="mkt-footer-bottom">
            <span>© 2026 Gantima Software. All rights reserved.</span>
            <span>
              <a href="/privacy" onClick={e=>{e.preventDefault();navigate('/privacy');}} style={{color:'#64748b'}}>Privacy Policy</a>
              {' · '}
              <a href="/terms" onClick={e=>{e.preventDefault();navigate('/terms');}} style={{color:'#64748b'}}>Terms of Service</a>
             
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
